/**
 * Shared scatter point coloring for D3 and uPlot explore scatter (keep rules in sync).
 */
import * as d3 from "d3";
import { sourcesStore } from "../store/sourcesStore";
import { getGroupKeyFromItem } from "./colorGrouping";
import { getExploreRowGroupKey } from "./exploreHistoryColorGroups";

export interface ScatterSeriesConfig {
  colorType?: string;
  color?: string;
  colorChannel?: { name?: string };
}

const twaHueScale = d3.scaleLinear<string>().domain([40, 90, 160]).range(["blue", "orange", "red"]);

/** Row value for color-by-channel: exact key, case-insensitive key (SQL aliases), or pre-mapped `color` from mapRowsToCharts. */
function colorByChannelRowValue(d: Record<string, unknown>, channelName: string | undefined): unknown {
  if (!channelName) {return undefined;}
  if (d[channelName] !== undefined) {return d[channelName];}
  const k = Object.keys(d).find((x) => x.toLowerCase() === channelName.toLowerCase());
  if (k !== undefined) {return d[k];}
  const pre = d.color;
  if (pre !== undefined && pre !== null && pre !== "") {return pre;}
  return undefined;
}

export interface ColorChannelLegendModel {
  channelName: string;
  min: number;
  max: number;
  gradientCss: string;
}

const COLOR_CHANNEL_LEGEND_STOPS = [0, 0.25, 0.5, 0.75, 1] as const;

/**
 * Heat color for a density fraction (count / max, 0..1), used by the performance scatter's
 * Color By DENSITY points and the timeline strip under it. sqrt spreads the sparse end, where most
 * of the cloud sits.
 */
export function densityHeatColor(fraction: number): string {
  const t = Number.isFinite(fraction) ? Math.min(1, Math.max(0, fraction)) : 0;
  return d3.interpolateViridis(Math.sqrt(t));
}

/** Viridis ramp for the density legends, sampled the same way as {@link densityHeatColor}. */
export function densityGradientCss(): string {
  const stops = COLOR_CHANNEL_LEGEND_STOPS.map((t) => `${densityHeatColor(t)} ${t * 100}%`);
  return `linear-gradient(to right, ${stops.join(", ")})`;
}

/**
 * Color By VMG (VMG % of target) on the performance scatter: blue (lowest, 0) → red (highest, 1).
 * Turbo trimmed at both ends so the extremes stay blue and red rather than its near-black tails,
 * and the middle (green / yellow) stays visible on light and dark backgrounds alike.
 */
export function vmgHeatColor(fraction: number): string {
  const t = Number.isFinite(fraction) ? Math.min(1, Math.max(0, fraction)) : 0;
  return d3.interpolateTurbo(0.1 + t * 0.82);
}

export function colorChannelGradientCss(
  scale: (value: number) => string,
  min: number,
  max: number
): string {
  const span = max - min;
  const stops = COLOR_CHANNEL_LEGEND_STOPS.map((t) => {
    const value = span === 0 ? min : min + t * span;
    return `${scale(value)} ${t * 100}%`;
  });
  return `linear-gradient(to right, ${stops.join(', ')})`;
}

export function buildColorChannelScale(
  filteredData: Record<string, unknown>[],
  colorChannelName: string | undefined
): ReturnType<typeof d3.scaleSequential<string>> | null {
  if (!colorChannelName) {return null;}
  const colorValues = filteredData
    .map((d) => colorByChannelRowValue(d, colorChannelName))
    .map((v) => (v !== undefined && v !== null ? Number(v) : NaN))
    .filter((n): n is number => !isNaN(n) && isFinite(n));
  if (colorValues.length === 0) {return null;}
  const colorExtent = d3.extent(colorValues);
  if (colorExtent[0] == null || colorExtent[1] == null) {return null;}
  // Green/blue for small values, yellow mid, red for large values (RdYlGn reversed)
  return d3.scaleSequential<string>().domain([colorExtent[0], colorExtent[1]]).interpolator((t) => d3.interpolateRdYlGn(1 - t));
}

export function buildColorChannelLegendModel(
  filteredData: Record<string, unknown>[],
  colorChannelName: string | undefined
): ColorChannelLegendModel | null {
  if (!colorChannelName) {return null;}
  const scale = buildColorChannelScale(filteredData, colorChannelName);
  if (!scale) {return null;}
  const domain = scale.domain();
  const min = domain[0];
  const max = domain[1];
  if (min == null || max == null || !Number.isFinite(min) || !Number.isFinite(max)) {
    return null;
  }
  return {
    channelName: colorChannelName,
    min,
    max,
    gradientCss: colorChannelGradientCss(scale, min, max),
  };
}

function getTwaValue(d: Record<string, unknown>, twaChannelName: string): number {
  const v = d[twaChannelName] ?? d.Twa;
  return typeof v === "number" ? v : Number(v) || 0;
}

/** Stable palette index from string (SOURCE / DAY fallbacks). */
function colorIndexForString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h * 31 + s.charCodeAt(i)) >>> 0;
  }
  return h % 10;
}

/**
 * Calendar day key YYYY-MM-DD for fleet / multi-day scatter (UTC from timestamps).
 */
export function getScatterDayKey(d: Record<string, unknown>): string {
  const rawDate = d.date;
  if (rawDate != null && String(rawDate).trim()) {
    const s = String(rawDate).trim();
    if (/^\d{8}$/.test(s)) {
      return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
    }
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
      return s.slice(0, 10);
    }
  }
  const dt = d.Datetime ?? d.datetime ?? d.timestamp;
  if (dt instanceof Date && !isNaN(dt.getTime())) {
    return dt.toISOString().slice(0, 10);
  }
  if (typeof dt === "string" && dt.length >= 10) {
    const parsed = Date.parse(dt);
    if (!isNaN(parsed)) {
      return new Date(parsed).toISOString().slice(0, 10);
    }
  }
  if (typeof dt === "number" && Number.isFinite(dt)) {
    return new Date(dt).toISOString().slice(0, 10);
  }
  return "unknown";
}

function scatterSourceName(d: Record<string, unknown>): string {
  const raw = d.source_name ?? d.SOURCE_NAME ?? d.Source_name;
  return String(raw ?? '').trim() || 'unknown';
}

export function buildSourceColorMapForScatter(rows: Record<string, unknown>[]): Record<string, string> {
  const names = new Set<string>();
  for (const r of rows) {
    names.add(scatterSourceName(r));
  }
  const sorted = [...names].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  const map: Record<string, string> = {};
  const scheme = d3.schemeCategory10;
  for (const name of sorted) {
    const project = sourcesStore.getSourceColor(name);
    map[name] = project || scheme[colorIndexForString(name)] || scheme[0]!;
  }
  return map;
}

export function buildDayColorMapForScatter(rows: Record<string, unknown>[]): Record<string, string> {
  const days = new Set<string>();
  for (const r of rows) {
    days.add(getScatterDayKey(r));
  }
  const sorted = [...days].sort();
  const ordinal = d3.scaleOrdinal(d3.schemeCategory10).domain(sorted);
  const map: Record<string, string> = {};
  for (const day of sorted) {
    map[day] = ordinal(day) as string;
  }
  return map;
}

export function getScatterPointColor(
  series: ScatterSeriesConfig,
  d: Record<string, unknown>,
  opts: {
    colortype: string;
    colorChannelScale: ReturnType<typeof d3.scaleSequential<string>> | null;
    twaChannelName: string;
    sourceColorMap?: Record<string, string>;
    dayColorMap?: Record<string, string>;
    categoryColorMap?: Record<string, string>;
  }
): string {
  const colortype = opts.colortype || "DEFAULT";

  if (colortype === "SOURCE") {
    const name = scatterSourceName(d);
    if (opts.sourceColorMap?.[name]) {
      return opts.sourceColorMap[name]!;
    }
    const project = sourcesStore.getSourceColor(name);
    return project || d3.schemeCategory10[colorIndexForString(name)]!;
  }

  if (colortype === "DAY") {
    const day = getScatterDayKey(d);
    if (opts.dayColorMap?.[day]) {
      return opts.dayColorMap[day]!;
    }
    return d3.schemeCategory10[colorIndexForString(day)]!;
  }

  if (colortype === "DEFAULT") {
    const seriesColorType = series.colorType || "Fixed";

    if (seriesColorType === "Fixed") {
      return series.color || d3.schemeCategory10[0]!;
    }
    if (seriesColorType === "By Channel") {
      const name = series.colorChannel?.name;
      const raw = name ? colorByChannelRowValue(d, name) : undefined;
      if (raw !== undefined) {
        const colorValue = Number(raw);
        if (!isNaN(colorValue) && isFinite(colorValue) && opts.colorChannelScale) {
          return opts.colorChannelScale(colorValue);
        }
      }
      return series.color || d3.schemeCategory10[0]!;
    }
    return series.color || d3.schemeCategory10[0]!;
  }

  if (colortype === "TACK") {
    const twaValue = getTwaValue(d, opts.twaChannelName);
    return twaValue > 0 ? "green" : "red";
  }

  if (colortype === "GRADE") {
    const grade = d.grade ?? d.Grade ?? d.GRADE;
    const g = typeof grade === "number" ? grade : Number(grade);
    if (g === 0) {return "lightgray";}
    if (g === 1) {return "red";}
    if (g === 2) {return "lightgreen";}
    if (g === 3) {return "darkgreen";}
    return "lightgray";
  }

  if (colortype === "UW/DW") {
    const twaValue = getTwaValue(d, opts.twaChannelName);
    const absTwa = Math.abs(twaValue || 0);
    if (absTwa < 75) {return "blue";}
    if (absTwa >= 75 && absTwa <= 120) {return "orange";}
    return "purple";
  }

  if (colortype === "EVENT" || colortype === "YEAR" || colortype === "CONFIG" || colortype === "CREW") {
    const key = getExploreRowGroupKey(d, colortype, opts.twaChannelName);
    if (opts.categoryColorMap?.[key]) {
      return opts.categoryColorMap[key]!;
    }
    const raw = getGroupKeyFromItem(d, colortype);
    const label = raw === undefined || raw === null ? "" : String(raw);
    return d3.schemeCategory10[colorIndexForString(label || key)]!;
  }

  const twaValue = getTwaValue(d, opts.twaChannelName);
  if (twaValue !== undefined && !isNaN(twaValue)) {
    return twaHueScale(Math.abs(twaValue));
  }
  return series.color || d3.schemeCategory10[0]!;
}

/**
 * Legend / fit-table swatch color aligned with scatter points and regression line:
 * for explore colortype DEFAULT + series Fixed, uses series.color (same as points);
 * otherwise uses the worker group color (TACK, GRADE, etc.).
 */
export function getFitTableLegendColor(
  series: ScatterSeriesConfig,
  exploreColortype: string | undefined,
  workerGroupColor: string
): string {
  const ct = exploreColortype || "DEFAULT";
  if (ct !== "DEFAULT") {return workerGroupColor;}
  const seriesColorType = series.colorType || "Fixed";
  if (seriesColorType === "Fixed") {
    return series.color || d3.schemeCategory10[0]!;
  }
  if (series.color) {return series.color;}
  return workerGroupColor;
}
